import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section
      data-testid="cta-section"
      className="py-20 md:py-28 bg-[var(--sv-secondary)] relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 0.5 } : {}}
          transition={{ duration: 1 }}
          className="absolute top-20 left-10 w-72 h-72 bg-[#4B4DF7]/20 rounded-full blur-[100px]"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 0.3 } : {}}
          transition={{ duration: 1, delay: 0.2 }}
          className="absolute bottom-10 right-10 w-96 h-96 bg-[#FF6550]/15 rounded-full blur-[120px]"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 0.2 } : {}}
          transition={{ duration: 1, delay: 0.4 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FFB74B]/10 rounded-full blur-[150px]"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-6"
          >
            <span className="text-sm font-medium text-slate-300">
              Pronto a trasformare la tua People Strategy?
            </span>
          </motion.div>

          <h2
            data-testid="cta-title"
            className="font-['Inter_Tight'] text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="block"
            >
              Scopri come l'AI può identificare
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="block bg-gradient-to-r from-[#8B8DF9] via-[#FF6550] to-[#FFB74B] bg-clip-text text-transparent"
            >
              il talento nascosto nella tua rete
            </motion.span>
          </h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto mb-8"
          >
            Come In's Mercato, costruisci una pipeline interna di talenti
            con assessment AI scalabili e oggettivi.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="flex flex-wrap items-center justify-center gap-4"
          >
            <a
              href="https://www.skillvue.ai/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-contact-btn"
            >
              <Button className="bg-[var(--sv-primary)] hover:bg-[var(--sv-primary-hover)] text-white rounded-full h-12 px-8 text-base font-semibold gap-2 shadow-lg shadow-[#4B4DF7]/25">
                Contattaci <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
            <a
              href="https://www.skillvue.ai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-website-btn"
            >
              <Button
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full h-12 px-8 text-base font-medium"
              >
                Visita Skillvue
              </Button>
            </a>
          </motion.div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 pt-8 border-t border-slate-700"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <img
                src="https://cdn.prod.website-files.com/63eb9c5c0665608db409b4df/66cdde23b308922774c4b3e0_logomark.svg"
                alt="Skillvue"
                className="h-6"
              />
              <span className="text-sm font-semibold text-white font-['Inter_Tight']">Skillvue</span>
            </div>
            <p className="text-xs text-slate-500" data-testid="footer-copyright">
              Case Study · Skillvue x In's Mercato
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.skillvue.ai/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                data-testid="footer-privacy"
              >
                Privacy Policy
              </a>
              <a
                href="https://www.skillvue.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                data-testid="footer-website"
              >
                skillvue.ai
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
